/**
 * Browser softphone via JsSIP + WebRTC microphone.
 * Registers as the mapped PBX extension and places/receives calls without a desk phone.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type {
  CallState,
  LogLevel,
  PhoneLog,
  SipState,
  Softphone,
  WsState,
} from "./softphone-types";
import { api } from "./api";
import { useSession } from "./session";
import { normalizeSipWsUrl, resolveSipWsForBrowser, sipWsCandidates } from "./sipWs";

type SoftphoneSession = {
  agent: {
    extension: string | null;
    phoneMode: "desk" | "sip";
    sipPasswordSet: boolean;
  };
  sip: {
    host: string;
    wsUri: string;
    uri: string;
    authorizationUser: string;
    displayName: string;
    password: string | null;
    registerExpires: number;
  };
  hints: {
    needsExtension: boolean;
    needsPassword: boolean;
    needsSipHost: boolean;
    needsSipWs: boolean;
    httpsNeedsWss: boolean;
  };
};

const Ctx = createContext<Softphone | null>(null);

function stamp() {
  return new Date().toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function nowIso() {
  return new Date().toISOString();
}

export function SoftphoneProvider({ children }: { children: React.ReactNode }) {
  const { user } = useSession();
  const [wsState, setWsState] = useState<WsState>("off");
  const [sipState, setSipState] = useState<SipState>("off");
  const [callState, setCallState] = useState<CallState>("idle");
  const [error, setError] = useState("");
  const [micNotice, setMicNotice] = useState("");
  const [remoteNumber, setRemoteNumber] = useState("");
  const [muted, setMuted] = useState(false);
  const [incoming, setIncoming] = useState<{ from: string; name?: string } | null>(
    null,
  );
  const [sipHost, setSipHost] = useState("");
  const [sipWsUrl, setSipWsUrl] = useState("");
  const [sipUri, setSipUri] = useState("");
  const [extension, setExtension] = useState("");
  const [hasPassword, setHasPassword] = useState(false);
  const [log, setLog] = useState<PhoneLog[]>([]);
  const [micOk, setMicOk] = useState(false);
  const [pbxStatus, setPbxStatus] = useState("");

  const uaRef = useRef<any>(null);
  const sessionRef = useRef<any>(null);
  const remoteAudioRef = useRef<HTMLAudioElement | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const holdInvitesRef = useRef(false);
  const sessionCacheRef = useRef<SoftphoneSession | null>(null);
  const passwordOverrideRef = useRef("");
  const regKeepaliveRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const pushLog = useCallback((msg: string, level: LogLevel = "info") => {
    setLog((prev) => {
      const next = [...prev, { at: stamp(), msg, level }];
      return next.slice(-80);
    });
  }, []);

  const clearRegKeepalive = useCallback(() => {
    if (regKeepaliveRef.current) {
      clearInterval(regKeepaliveRef.current);
      regKeepaliveRef.current = null;
    }
  }, []);

  const startRegKeepalive = useCallback(
    (ua: any) => {
      clearRegKeepalive();
      regKeepaliveRef.current = setInterval(() => {
        try {
          if (ua?.isRegistered?.()) {
            ua.register();
            pushLog("SIP re-REGISTER (keep Available on PBX)", "info");
          }
        } catch {
          /* ignore */
        }
      }, 25000);
    },
    [clearRegKeepalive, pushLog],
  );

  const syncPbxLogin = useCallback(
    async (ext: string, opts?: { poll?: boolean }) => {
      try {
        const data = await api<{
          extension: string;
          pbxStatus: string | null;
          available: boolean | null;
          message: string;
          mqttOk: boolean;
        }>("/softphone/pbx-ready", {
          method: "POST",
          body: JSON.stringify({
            extension: ext,
            poll: opts?.poll !== false,
          }),
        });
        if (data.pbxStatus) setPbxStatus(String(data.pbxStatus));
        const avail =
          data.available === true ||
          (data.pbxStatus &&
            !/unavail|offline|logout/i.test(String(data.pbxStatus)));
        pushLog(
          data.message +
            (data.pbxStatus ? ` · LuCI / extension_list: ${data.pbxStatus}` : ""),
          avail ? "ok" : "warn",
        );
        return data;
      } catch (err) {
        pushLog(
          "PBX API sync skipped: " +
            (err instanceof Error ? err.message : String(err)),
          "warn",
        );
        return null;
      }
    },
    [pushLog],
  );

  function ensureRemoteAudio() {
    if (typeof document === "undefined") return null;
    if (!remoteAudioRef.current) {
      const el = document.createElement("audio");
      el.autoplay = true;
      el.setAttribute("playsinline", "true");
      el.style.display = "none";
      document.body.appendChild(el);
      remoteAudioRef.current = el;
    }
    return remoteAudioRef.current;
  }

  function attachRemote(session: any) {
    const audio = ensureRemoteAudio();
    if (!audio) return;
    const bind = (stream: MediaStream) => {
      audio.srcObject = stream;
      void audio.play().catch(() => {
        /* autoplay may need a user gesture — already had one for Call */
      });
    };
    session.connection?.addEventListener("addstream", (e: any) => {
      if (e.stream) bind(e.stream);
    });
    session.connection?.addEventListener("track", (e: any) => {
      if (e.streams?.[0]) bind(e.streams[0]);
    });
    try {
      const receivers = session.connection?.getReceivers?.() || [];
      const tracks = receivers.map((r: RTCRtpReceiver) => r.track).filter(Boolean);
      if (tracks.length) bind(new MediaStream(tracks));
    } catch {
      /* ignore */
    }
  }

  function wireSession(session: any) {
    sessionRef.current = session;
    attachRemote(session);

    session.on("progress", () => {
      setCallState("ringing");
      pushLog("Ringing…", "info");
    });
    session.on("accepted", () => {
      setCallState("active");
      setIncoming(null);
      pushLog("Call answered", "ok");
      attachRemote(session);
    });
    session.on("confirmed", () => {
      setCallState("active");
      attachRemote(session);
    });
    session.on("ended", () => {
      setCallState("ended");
      setIncoming(null);
      setRemoteNumber("");
      sessionRef.current = null;
      pushLog("Call ended", "info");
      setTimeout(() => setCallState("idle"), 800);
    });
    session.on("failed", (e: any) => {
      setCallState("failed");
      setIncoming(null);
      sessionRef.current = null;
      const cause = e?.cause || e?.message || "failed";
      setError(String(cause));
      pushLog(`Call failed: ${cause}`, "error");
      setTimeout(() => setCallState("idle"), 1200);
    });
  }

  async function fetchSession(): Promise<SoftphoneSession> {
    const data = await api<SoftphoneSession>("/softphone");
    sessionCacheRef.current = data;
    setSipHost(data.sip.host || "");
    setSipWsUrl(data.sip.wsUri || "");
    setSipUri(data.sip.uri || "");
    setExtension(data.agent.extension || user?.extension || "");
    setHasPassword(Boolean(data.sip.password) || data.agent.sipPasswordSet);
    return data;
  }

  const enableMic = useCallback(async () => {
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        setMicNotice("This browser does not support microphone access.");
        setMicOk(false);
        return false;
      }
      // Plain audio:true — avoid stale deviceId "Requested device not found"
      let stream: MediaStream | null = null;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
          video: false,
        });
      } catch {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: false,
        });
      }
      localStreamRef.current?.getTracks().forEach((t) => t.stop());
      localStreamRef.current = stream;
      setMicOk(true);
      setMicNotice("");
      pushLog("Microphone allowed", "ok");
      return true;
    } catch (err) {
      setMicOk(false);
      const msg =
        err instanceof Error
          ? err.message
          : "Microphone permission denied — allow mic for softphone audio.";
      // Don't surface NotFound during connect noise
      if (!/requested device not found/i.test(msg)) {
        setMicNotice(msg);
      } else {
        setMicNotice(
          "No microphone found — plug headset, set it as default in Windows Sound, then Allow mic.",
        );
      }
      pushLog("Microphone blocked: " + msg, "error");
      return false;
    }
  }, [pushLog]);

  const disconnect = useCallback(() => {
    clearRegKeepalive();
    try {
      sessionRef.current?.terminate?.();
    } catch {
      /* ignore */
    }
    sessionRef.current = null;
    try {
      uaRef.current?.stop?.();
    } catch {
      /* ignore */
    }
    uaRef.current = null;
    setWsState("off");
    setSipState("off");
    setCallState("idle");
    setIncoming(null);
    setRemoteNumber("");
    setPbxStatus("");
    pushLog("Softphone disconnected", "info");
  }, [clearRegKeepalive, pushLog]);

  const connect = useCallback(
    async (password: string) => {
      setError("");
      setMicNotice("");
      passwordOverrideRef.current = password || passwordOverrideRef.current;

      let session: SoftphoneSession;
      try {
        session = await fetchSession();
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Could not load softphone session";
        setError(msg);
        pushLog(msg, "error");
        return false;
      }

      const pass = passwordOverrideRef.current || session.sip.password || "";
      if (!session.agent.extension) {
        setError("Map a PBX extension to your user first (Users).");
        return false;
      }
      if (!session.sip.host) {
        setError("Admin: set SIP host (e.g. 192.168.0.180) in Softphone settings.");
        return false;
      }
      if (!pass) {
        setError("Enter the SIP password for your extension (same as on Neron).");
        return false;
      }

      const candidates = sipWsCandidates(session.sip.host, session.sip.wsUri);
      const preferred = resolveSipWsForBrowser(session.sip.host, session.sip.wsUri);
      if (!candidates.length) {
        setError("Admin: set SIP WebSocket URL (e.g. ws://192.168.0.180:8088/ws).");
        return false;
      }

      // HTTPS page cannot use ws://
      const usable = candidates.filter((url) => {
        if (
          typeof window !== "undefined" &&
          window.location.protocol === "https:" &&
          url.toLowerCase().startsWith("ws://")
        ) {
          return false;
        }
        return true;
      });
      if (!usable.length) {
        setError(
          "This site is HTTPS — need wss:// WebSocket. On LAN open http://192.168.0.151:3000 and use ws://192.168.0.180:8088/ws",
        );
        return false;
      }

      disconnect();

      // MQTT API: extension_set logout_status=0 before REGISTER (Neron login gate)
      void syncPbxLogin(String(session.agent.extension), { poll: false });

      let JsSIP: any;
      try {
        JsSIP = (await import("jssip")).default;
      } catch (err) {
        setError("JsSIP failed to load");
        pushLog(String(err), "error");
        return false;
      }

      const startUa = (wsUri: string) =>
        new Promise<boolean>((resolve) => {
          setWsState("connecting");
          setSipState("registering");
          pushLog(`Connecting ${wsUri}`, "info");

          let settled = false;
          let failedTip = "";
          let timer: ReturnType<typeof setInterval> | undefined;
          const finish = (ok: boolean) => {
            if (settled) return;
            settled = true;
            if (timer) clearInterval(timer);
            resolve(ok);
          };

          try {
            try {
              uaRef.current?.stop?.();
            } catch {
              /* ignore */
            }

            const socket = new JsSIP.WebSocketInterface(wsUri);
            // Asterisk stores Contact with transport=WS and qualifies over this socket.
            // Lowercase "ws" is fine; via must be WS/WSS (not UDP).
            try {
              socket.via_transport = wsUri.toLowerCase().startsWith("wss")
                ? "WSS"
                : "WS";
            } catch {
              /* ignore */
            }

            // Contact MUST be random@*.invalid;transport=ws (JsSIP/Asterisk WebRTC style).
            // Using sip:1002@IP makes Neron rewrite to UDP-looking Contact → Unavailable.
            const contactToken = Math.random().toString(36).slice(2, 12);
            const contactHost = `${contactToken}.invalid`;
            const contactUri = `sip:${contactToken}@${contactHost};transport=ws`;

            const ua = new JsSIP.UA({
              sockets: [socket],
              uri:
                session.sip.uri ||
                `sip:${session.agent.extension}@${session.sip.host}`,
              password: pass,
              authorization_user:
                session.sip.authorizationUser || session.agent.extension,
              display_name:
                session.sip.displayName ||
                user?.name ||
                session.agent.extension,
              contact_uri: contactUri,
              session_timers: false,
              register: true,
              register_expires: 120,
              // preloaded Route can break WS qualify on some Asterisk/Neron builds
              use_preloaded_route: false,
              connection_recovery_min_interval: 2,
              connection_recovery_max_interval: 20,
            });
            uaRef.current = ua;

            // Force REGISTER Contact string — must include transport=ws or LuCI stays Unavailable
            try {
              const reg = (ua as any)._registrator;
              const instance = ua.configuration?.instance_id;
              if (reg) {
                reg._contact =
                  `<sip:${contactToken}@${contactHost};transport=ws>` +
                  `;+sip.ice;reg-id=1;+sip.instance="<urn:uuid:${instance}>"`;
              }
              pushLog(`SIP Contact ${contactUri}`, "info");
            } catch {
              /* ignore */
            }

            ua.on("connecting", () => setWsState("connecting"));
            ua.on("connected", () => {
              setWsState("connected");
              pushLog("WebSocket connected " + wsUri, "ok");
              try {
                if (!ua.isRegistered()) ua.register();
              } catch {
                /* ignore */
              }
            });
            ua.on("disconnected", () => {
              clearRegKeepalive();
              if (!settled) {
                failedTip = failedTip || "WebSocket disconnected";
                finish(false);
              } else {
                setWsState("failed");
                setSipState("failed");
              }
              pushLog("WebSocket disconnected", "warn");
            });
            ua.on("registered", () => {
              setSipState("registered");
              setError("");
              startRegKeepalive(ua);
              const shown =
                (ua as any)._registrator?._contact ||
                ua.contact?.toString?.() ||
                contactUri;
              pushLog(
                `Registered ${session.agent.extension} — Contact ${shown}`,
                "ok",
              );
              finish(true);
            });
            ua.on("unregistered", () => {
              setSipState("off");
              pushLog("Unregistered", "warn");
            });
            ua.on("registrationFailed", (e: any) => {
              const cause = e?.cause || "registration failed";
              const status = e?.response?.status_code;
              failedTip = status ? `${cause} (${status})` : String(cause);
              setError(
                /forbidden|unauthorized|401|403/i.test(failedTip)
                  ? `Wrong SIP password for ${session.agent.extension}`
                  : `REGISTER failed: ${failedTip}`,
              );
              pushLog(`REGISTER failed on ${wsUri}: ${failedTip}`, "error");
              finish(false);
            });
            ua.on("newRTCSession", (e: any) => {
              const sessionRtc = e.session;
              if (sessionRtc.direction !== "incoming") return;
              if (holdInvitesRef.current) {
                try {
                  sessionRtc.terminate({ status_code: 486 });
                } catch {
                  /* ignore */
                }
                return;
              }
              const from =
                sessionRtc.remote_identity?.uri?.user ||
                sessionRtc.remote_identity?.display_name ||
                "unknown";
              setIncoming({ from: String(from) });
              setRemoteNumber(String(from));
              setCallState("ringing");
              pushLog(`Incoming from ${from}`, "info");
              wireSession(sessionRtc);
            });

            ua.start();

            const started = Date.now();
            timer = setInterval(() => {
              if (ua.isRegistered?.()) finish(true);
              else if (failedTip) finish(false);
              else if (Date.now() - started > 10000) finish(false);
            }, 200);
          } catch (err) {
            const msg = err instanceof Error ? err.message : "Connect failed";
            setError(msg);
            finish(false);
          }
        });

      for (const wsUri of usable) {
        sessionCacheRef.current = {
          ...session,
          sip: { ...session.sip, wsUri },
        };
        setSipWsUrl(wsUri);
        const ok = await startUa(wsUri);
        if (ok) {
          setSipState("registered");
          setWsState("connected");
          if (wsUri !== preferred) {
            pushLog(`Using WebSocket ${wsUri} (LAN preferred over cloud wss)`, "ok");
          }
          // After SIP REGISTER: re-apply login + poll until LuCI leaves Unavailable
          void (async () => {
            await new Promise((r) => setTimeout(r, 800));
            let last = await syncPbxLogin(String(session.agent.extension), {
              poll: true,
            });
            for (let i = 0; i < 4; i++) {
              if (
                last?.available === true ||
                (last?.pbxStatus &&
                  !/unavail|offline|logout/i.test(String(last.pbxStatus)))
              ) {
                break;
              }
              await new Promise((r) => setTimeout(r, 2000));
              last = await syncPbxLogin(String(session.agent.extension), {
                poll: true,
              });
            }
          })();
          return true;
        }
      }

      setSipState("failed");
      setWsState("failed");
      setError(
        `Could not register ${session.agent.extension}. On LAN use ws://${session.sip.host}:8088/ws (not cloud wss). Check SIP password.`,
      );
      return false;
    },
    [clearRegKeepalive, disconnect, pushLog, startRegKeepalive, syncPbxLogin, user?.name],
  );

  const ensureRegistered = useCallback(
    async (password?: string) => {
      if (sipState === "registered" && uaRef.current) return;
      const ok = await connect(password || passwordOverrideRef.current || "");
      if (!ok) throw new Error(error || "Could not register softphone");
      // wait briefly for registered event
      await new Promise<void>((resolve, reject) => {
        const started = Date.now();
        const id = setInterval(() => {
          if (uaRef.current?.isRegistered?.()) {
            clearInterval(id);
            resolve();
          } else if (Date.now() - started > 12000) {
            clearInterval(id);
            reject(new Error("SIP registration timed out"));
          }
        }, 200);
      });
    },
    [connect, error, sipState],
  );

  const call = useCallback(
    async (number: string) => {
      const digits = String(number || "").replace(/[^\d*#]/g, "");
      if (!digits) throw new Error("Enter a number to dial");
      await ensureRegistered(passwordOverrideRef.current);
      const ua = uaRef.current;
      if (!ua?.isRegistered?.()) throw new Error("Softphone is not registered");

      const host = sessionCacheRef.current?.sip.host || sipHost;
      const target = digits.includes("@") ? digits : `sip:${digits}@${host}`;
      setRemoteNumber(digits);
      setCallState("inviting");
      pushLog(`Calling ${digits}`, "info");

      const options = {
        mediaConstraints: { audio: true, video: false },
        pcConfig: { iceServers: [{ urls: "stun:stun.l.google.com:19302" }] },
      };
      const session = ua.call(target, options);
      wireSession(session);
    },
    [ensureRegistered, pushLog, sipHost],
  );

  const answer = useCallback(async () => {
    const session = sessionRef.current;
    if (!session) return;
    const okMic = micOk || (await enableMic());
    if (!okMic) return;
    session.answer({
      mediaConstraints: { audio: true, video: false },
      pcConfig: { iceServers: [{ urls: "stun:stun.l.google.com:19302" }] },
    });
    attachRemote(session);
    setCallState("active");
    setIncoming(null);
  }, [enableMic, micOk]);

  const hangup = useCallback(() => {
    try {
      sessionRef.current?.terminate?.();
    } catch {
      /* ignore */
    }
    sessionRef.current = null;
    setIncoming(null);
    setCallState("idle");
    setRemoteNumber("");
  }, []);

  const mute = useCallback((next: boolean) => {
    const session = sessionRef.current;
    try {
      if (next) session?.mute?.({ audio: true });
      else session?.unmute?.({ audio: true });
    } catch {
      /* ignore */
    }
    localStreamRef.current?.getAudioTracks().forEach((t) => {
      t.enabled = !next;
    });
    setMuted(next);
  }, []);

  const sendDtmf = useCallback((digit: string) => {
    try {
      sessionRef.current?.sendDTMF?.(digit);
    } catch {
      /* ignore */
    }
  }, []);

  const playPrompt = useCallback(async (_url: string) => {
    pushLog("Voice prompts not available on softphone yet", "warn");
  }, [pushLog]);

  const armPbxInvite = useCallback(() => {
    holdInvitesRef.current = false;
  }, []);

  const holdRemoteInvites = useCallback((hold: boolean) => {
    holdInvitesRef.current = hold;
  }, []);

  useEffect(() => {
    setExtension(user?.extension || "");
    setHasPassword(Boolean(user?.sipPasswordSet));
  }, [user?.extension, user?.sipPasswordSet]);

  useEffect(() => {
    return () => {
      disconnect();
      localStreamRef.current?.getTracks().forEach((t) => t.stop());
      if (remoteAudioRef.current) {
        remoteAudioRef.current.srcObject = null;
        remoteAudioRef.current.remove();
        remoteAudioRef.current = null;
      }
    };
  }, [disconnect]);

  const refreshPbxStatus = useCallback(async () => {
    const ext = extension || user?.extension || "";
    if (!ext) return;
    try {
      const data = await api<{
        pbxStatus: string | null;
        available: boolean | null;
      }>(`/softphone/extension-status?extension=${encodeURIComponent(ext)}`);
      if (data.pbxStatus) setPbxStatus(String(data.pbxStatus));
      pushLog(
        `LuCI / extension_list status: ${data.pbxStatus || "unknown"}`,
        data.available === false ? "warn" : "ok",
      );
    } catch (err) {
      pushLog(
        "Could not read PBX extension status: " +
          (err instanceof Error ? err.message : String(err)),
        "warn",
      );
    }
  }, [extension, pushLog, user?.extension]);

  const value = useMemo<Softphone>(
    () => ({
      wsState,
      sipState,
      callState,
      error,
      micNotice,
      remoteNumber,
      muted,
      incoming,
      sipHost,
      sipWsUrl,
      sipUri,
      extension,
      hasPassword,
      log,
      micOk,
      pbxStatus,
      enableMic,
      connect,
      disconnect,
      call,
      answer,
      hangup,
      mute,
      sendDtmf,
      playPrompt,
      armPbxInvite,
      holdRemoteInvites,
      ensureRegistered,
      refreshPbxStatus,
    }),
    [
      wsState,
      sipState,
      callState,
      error,
      micNotice,
      remoteNumber,
      muted,
      incoming,
      sipHost,
      sipWsUrl,
      sipUri,
      extension,
      hasPassword,
      log,
      micOk,
      pbxStatus,
      enableMic,
      connect,
      disconnect,
      call,
      answer,
      hangup,
      mute,
      sendDtmf,
      playPrompt,
      armPbxInvite,
      holdRemoteInvites,
      ensureRegistered,
      refreshPbxStatus,
    ],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSoftphone() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useSoftphone outside provider");
  return ctx;
}

export type {
  Softphone,
  WsState,
  SipState,
  CallState,
  LogLevel,
  PhoneLog,
} from "./softphone-types";
